// The sort a search runs with, and the one it starts on.
//
// Three places each had their own idea of the starting sort: the Sort By control
// showed "Total Nightly Rate — High to Low", the cards on a page were ordered by
// the same hard-coded value, and the request sent NO sort at all and left it to
// the hub, whose own default is price High to Low. An agent who had never touched
// Sort By therefore opened every search on the most expensive villas.
//
// Moriya, 2 Oct 2026: "the default will be from low to high and not the other way
// around — when everybody sees only properties at 100,000 they don't even continue
// to search."
//
// One default, read through one function, so the label, the order of the cards and
// the request cannot disagree. An agent's own choice (stored by SortByDropdown)
// always wins over it.

export const DEFAULT_SORT = Object.freeze({
  sortBy: "data.prices.basePrice:1",
  title: "Total Nightly Rate",
  direction: "Low to High",
});

// "Best Fit" (VT review 3/8: "a 12-guest search opens on 8-bedroom estates for 18 people. There
// is no relevance or best-fit option"). The search already keeps only the villas that sleep the
// group, so ordering those by how many they sleep, fewest first, puts the villas closest to the
// group's size at the top: 12 guests see the villas for 12, then for 13, 14 and so on. The hub
// sorts on any one stored field, so this needs nothing new from it, and the order is the hub's
// alone: a page of cards is not sorted again for it (store/redux/Property/actions.js leaves a
// sort it has no rule for as it came). On-request villas follow the priced ones, as in every sort.
export const BEST_FIT_SORT = Object.freeze({
  sortBy: "data.accommodates:1",
  title: "Best Fit",
  direction: "For Your Guests",
});

// What each stored sort reads as when its label was not stored with it.
const LABELS = {
  [BEST_FIT_SORT.sortBy]: { title: BEST_FIT_SORT.title, direction: BEST_FIT_SORT.direction },
  "data.prices.basePrice:1": { title: "Total Nightly Rate", direction: "Low to High" },
  "data.prices.basePrice:-1": { title: "Total Nightly Rate", direction: "High to Low" },
  "data.bedrooms:1": { title: "Bedrooms", direction: "Low to High" },
  "data.bedrooms:-1": { title: "Bedrooms", direction: "High to Low" },
};

// A title stored under its old wording reads as the new one. "Number of BD" was the bedrooms
// sort until 6 Oct 2026 (VT review 3/8, H: the label was not understood).
const RENAMED_TITLES = { "Number of BD": "Bedrooms" };

const read = (key) => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

// "field:1" or "field:-1" — anything else is not a sort the search can run.
const isSort = (value) => typeof value === "string" && /^[A-Za-z_][\w.]*:-?1$/.test(value);

/** The sort in force: the agent's stored choice, else the default. */
export const currentSortBy = () => {
  const stored = read("sortBy");
  return isSort(stored) ? stored : DEFAULT_SORT.sortBy;
};

/**
 * What the Sort By control shows for the sort in force.
 *
 * The label is only taken from storage when a sort is stored with it. A label on
 * its own is left over from before 2 Oct 2026, when the fresh-search reset removed
 * `sortBy` and left `sortTitle` / `sortLowToHigh` behind: the control then showed
 * "Number of BD — Low to High" over a search that was not sorted that way.
 */
export const currentSortLabel = () => {
  const stored = read("sortBy");
  if (!isSort(stored)) return { title: DEFAULT_SORT.title, direction: DEFAULT_SORT.direction };
  const known = LABELS[stored] || {};
  return {
    // "Total Commission" shares its sort with "Total Nightly Rate, High to Low", so
    // the stored title is what tells them apart.
    title: RENAMED_TITLES[read("sortTitle")] || read("sortTitle") || known.title || DEFAULT_SORT.title,
    direction: read("sortLowToHigh") || known.direction || DEFAULT_SORT.direction,
  };
};
